import { useEffect, useState } from "react";
import { ProgressBar, ProgressLabel, ProgressRoot } from "./ui/progress";
import { Box } from "@chakra-ui/react";

const dbName = "tnc-coral";
const storeName = "requestQueue";
const storeKey = "request";

type Store = {
  name: string;
  keyPath: string;
}

function initIndexedDb (dbName: string, stores: Store[]) {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(dbName, 3);
    request.onerror = (event) => {
      reject((event.target as IDBOpenDBRequest).error);
    };
    request.onsuccess = (event) => {
      resolve((event.target as IDBOpenDBRequest).result);
    };
    request.onupgradeneeded = (event) => {
      stores.forEach((store) => {
        (event.target as IDBOpenDBRequest).result.createObjectStore(store.name, { autoIncrement: true });
      });
    };
  });
}

function deserialize(data: string) {
  const { url, ...requestData } = JSON.parse(data);
  return new Request(url, requestData);
}

function RequestQueueHandler() {
  const [queuedRequests, setQueuedRequests] = useState<number>(0);
  const [uploadedRequests, setUploadedRequests] = useState<number>(0);

  const flushRequests = () => {
    initIndexedDb(dbName, [{ name: storeName, keyPath: storeKey }])
      .then((db) => {
        const store = db.transaction([storeName], "readwrite").objectStore(storeName);
        const request = store.getAllKeys();
        request.onsuccess = (event) => {
          const keys = (event.target as IDBRequest).result;
          setQueuedRequests(keys.length);

          for (let i = 0, len = keys.length; i < len; i++) {
            const keyRequest = store.get(keys[i]);
            keyRequest.onsuccess = (event) => {
              const request = deserialize((event.target as IDBRequest).result);

              // Update the auth token
              const headers = new Headers(request.headers);
              const token = localStorage.getItem("token");
              if (token) {
                headers.set("Authorization", `Token ${token}`);
              } else {
                headers.delete("Authorization");
              }

              fetch(new Request(request, { headers }))
                .then(res => {
                  if (res.ok) {
                    db.transaction([storeName], "readwrite").objectStore(storeName).delete(keys[i]);
                  }
                })
                .finally(() => setUploadedRequests(prev => prev + 1));
            };
          }
        };
      });
  };

  useEffect(() => {
    window.addEventListener("online", flushRequests);

    return () => {
      window.removeEventListener("online", flushRequests);
    };
  }, []);

  useEffect(() => {
    if (queuedRequests > 0 && queuedRequests === uploadedRequests) {
      const timeout = setTimeout(() => {
        setQueuedRequests(0);
        setUploadedRequests(0);
      }, 2000);

      return () => clearTimeout(timeout);
    }
  }, [queuedRequests, uploadedRequests]);

  if (queuedRequests > 0) {
    return (
      <Box p="4" bgColor="gray.200">
        <ProgressRoot min={0} max={queuedRequests} value={uploadedRequests}>
          <ProgressLabel>
            Synching {queuedRequests} updates
          </ProgressLabel>
          <ProgressBar />
        </ProgressRoot>
      </Box>
    );
  }

  return null;
}

export default RequestQueueHandler;
